import { UseFormRegister, FieldErrors, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import type { CreateTripInput } from "@/lib/validations";

interface DatesAccordionProps {
  register: UseFormRegister<CreateTripInput>;
  errors: FieldErrors<CreateTripInput>;
  watch: UseFormWatch<CreateTripInput>;
  setValue: UseFormSetValue<CreateTripInput>;
  routeCount: number;
  routeLabels: string[];
}

const today = new Date().toISOString().split("T")[0];

export default function DatesAccordion({
  register,
  errors,
  watch,
  setValue,
  routeCount,
  routeLabels,
}: DatesAccordionProps) {
  return (
    <AccordionItem value="dates">
      <AccordionTrigger>Dates &amp; times</AccordionTrigger>
      <AccordionContent className="space-y-5">
        <p className="text-xs text-muted-foreground">
          Set the departure date and time preference for each leg. These stay in sync with the
          Routes section.
        </p>
        {Array.from({ length: routeCount }).map((_, index) => {
          const timeType =
            (watch(`routes.${index}.timeConstraint.type` as const) as string) || "any";
          const routeErrors = errors.routes?.[index];
          return (
            <div key={index} className="space-y-3 rounded-md border border-border p-4">
              <p className="text-sm font-medium">{routeLabels[index] || `Leg ${index + 1}`}</p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor={`dates-departure-${index}`}>Departure date</Label>
                  <Input
                    id={`dates-departure-${index}`}
                    type="date"
                    min={today}
                    {...register(`routes.${index}.departureDate` as const)}
                  />
                  {routeErrors?.departureDate && (
                    <p className="text-xs text-destructive" role="alert">
                      {routeErrors.departureDate.message as string}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor={`dates-time-${index}`}>Time preference</Label>
                  <Select
                    value={timeType}
                    onValueChange={(val) =>
                      setValue(`routes.${index}.timeConstraint.type` as const, val as never)
                    }
                  >
                    <SelectTrigger id={`dates-time-${index}`}>
                      <SelectValue placeholder="Any time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any time</SelectItem>
                      <SelectItem value="after">Depart after</SelectItem>
                      <SelectItem value="before">Arrive before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {timeType !== "any" && (
                  <div className="space-y-2">
                    <Label htmlFor={`dates-time-value-${index}`}>
                      {timeType === "after" ? "Depart after" : "Arrive before"}
                    </Label>
                    <Input
                      id={`dates-time-value-${index}`}
                      type="time"
                      {...register(`routes.${index}.timeConstraint.time` as const)}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </AccordionContent>
    </AccordionItem>
  );
}
